import { getMemberExternalLoginUrl } from './memberExternalLogin';

export class NavigationRedirectError extends Error {
  constructor(message = 'Navigating to login...') {
    super(message);
    this.name = 'NavigationRedirectError';
  }
}

export function isNavigationRedirectError(error: unknown): error is NavigationRedirectError {
  return (
    error instanceof NavigationRedirectError ||
    (error as { name?: string } | null)?.name === 'NavigationRedirectError'
  );
}

function navigateTo(url?: string | null): never {
  const target = url ?? getMemberExternalLoginUrl();
  window.location.replace(target);
  throw new NavigationRedirectError();
}

type FetchJsonOptions = RequestInit & {
  expectJson?: boolean;
  suppressLoginRedirect?: boolean;
};

export async function fetchWithLoginRedirect<T = unknown>(
  input: RequestInfo | URL,
  init: FetchJsonOptions = {},
): Promise<T> {
  const {
    expectJson = true,
    suppressLoginRedirect = false,
    headers,
    redirect = 'manual',
    credentials = 'include',
    ...rest
  } = init;

  const requestHeaders = new Headers(headers);
  if (!requestHeaders.has('Accept')) {
    requestHeaders.set('Accept', 'application/json');
  }

  if (!requestHeaders.has('X-DA-API')) {
    requestHeaders.set('X-DA-API', '1');
  }

  let response: Response;

  try {
    response = await fetch(input, { headers: requestHeaders, credentials, redirect, ...rest });
  } catch (error) {
    if (!suppressLoginRedirect) {
      navigateTo(getMemberExternalLoginUrl());
    }

    throw error;
  }

  if (response.type === 'opaqueredirect') {
    if (!suppressLoginRedirect) {
      navigateTo(getMemberExternalLoginUrl());
    }

    return response as T;
  }

  if (response.status >= 300 && response.status < 400) {
    if (!suppressLoginRedirect) {
      const location = response.headers.get('Location');
      navigateTo(location ?? getMemberExternalLoginUrl());
    }

    return response as T;
  }

  if (response.status === 401 || response.status === 403) {
    if (!suppressLoginRedirect) {
      const headerRedirect = response.headers.get('X-Login-Redirect');
      if (headerRedirect) {
        navigateTo(headerRedirect);
      }

      const authenticateHeader = response.headers.get('WWW-Authenticate');
      const uriMatch = authenticateHeader?.match(/uri="([^"]+)"/i);
      if (uriMatch?.[1]) {
        navigateTo(uriMatch[1]);
      }

      navigateTo(getMemberExternalLoginUrl());
    }

    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    let text = '';
    try {
      text = await response.clone().text();
    } catch {
      // Ignore response body read errors and fall back to status text below.
    }

    throw new Error(text || response.statusText || `HTTP ${response.status}`);
  }

  if (!expectJson) {
    return response as T;
  }

  const contentType = response.headers.get('Content-Type') || '';
  if (!contentType.includes('application/json')) {
    throw new Error('Unexpected content type');
  }

  return response.json() as Promise<T>;
}
